import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import * as equalsOperator from "@arcgis/core/geometry/operators/equalsOperator.js";
import { toast } from "react-toastify";

import {
    computeChangedFieldsVsDefault,
    evaluateFastPath,
    fastPathReasonText,
    FieldChange,
    SECONDARY_ONLY_IGNORED_FIELDS,
    buildChangedSectorMap,
    lightHasChangedSectors,
} from "./directToDefaultUtils";
import { postFeaturesToDefault, getDifferencesObjectIds } from "./newVersioningUtils";
import { getStatusFieldName, getValidationResult, NFSStatus } from "./nfsUtils";
import { getAllRelatedFeatures, updateFeatureAttributes } from "./arcgisUtils";
import { exportFeatureToSksd } from "./sksdUtils";
import { getDefaultAndOperativeFeature, getAllLightHouseFeatures } from "./versioningUtils";
import { createFailedSksdJobForFeature } from "./jobsUtils";
import { FeatureClasses, NauticalClasses } from "../constants/NauticalObjects";
import { JaNei } from "../constants/nfsDomains";
import { getMapView } from "./arcgisUtils";
import { uuidv4 } from "./helpers";
import { AppConfig } from "../AppConfig";
import type Graphic from "@arcgis/core/Graphic";
import type Geometry from "@arcgis/core/geometry/Geometry";
import type Relationship from "@arcgis/core/layers/support/Relationship";

export type FastPathResult =
    | { ok: true; sksdSent: boolean; hadSecondary: boolean }
    | { ok: false; reason: "not-allowed"; details: string }
    | { ok: false; reason: "validation"; errors: string[] }
    | { ok: false; reason: "post" }
    | {
          ok: false;
          reason: "sksd";
          primaryRecoveryJobId: string | null;
      };

const PROGRESS_TOAST_ID = "fast-path-progress";

/**
 * Config-driven options for `evaluateFastPath`. The UI gate and the orchestrator re-gate
 * both go through here so they always apply the same rule.
 */
export const fastPathConfigOptions = (): { allowSksdOnProjectedExisting: boolean } => ({
    allowSksdOnProjectedExisting: AppConfig.FeatureToggles?.FastPathSksdOnProjectedExisting === true,
});

// react-toastify v11 closes a toast as soon as a controlled `progress` reaches 1, even
// when `autoClose: false` is set — so terminal error/warning updates must omit `progress`
// and hide the bar instead, otherwise the message flashes for a frame and disappears.
const finalizeProgressToast = (
    render: string,
    type: "error" | "warning" | "success",
    autoClose: number | false = false
) => {
    toast.update(PROGRESS_TOAST_ID, {
        render,
        type,
        autoClose,
        hideProgressBar: true,
    });
};

export type FastPathPair = {
    /** Always the Lys-side feature when a pair is involved; otherwise the selected feature. */
    primary: Graphic;
    /** The IB/Flytendemerke partner if one exists, else null. */
    secondary: Graphic | null;
    /**
     * True when a partner was expected but could not be resolved (lookup failed, mandatory
     * partner absent, or a linked secondary whose Lys was not found). The pair is then
     * incomplete and must not be promoted to OPERATIVE as if it were a single object.
     */
    partnerMissing: boolean;
};

const isLinkedSecondary = (layerId: number | undefined, attributes: Record<string, any> | undefined): boolean => {
    if (!attributes) return false;
    if (layerId === FeatureClasses.IB) {
        return attributes.tilknyttet_hib == JaNei.Ja || attributes.tilknyttet_fl_la == JaNei.Ja;
    }
    if (layerId === FeatureClasses.Flytendemerke) return attributes.tilknyttet_lysboye == JaNei.Ja;
    return false;
};

const bindLayerById = (feature: Graphic, layerId: number): boolean => {
    const view = getMapView();
    const layer = view?.map?.layers.find((l) => l.type === "feature" && (l as FeatureLayer).layerId === layerId) as
        | FeatureLayer
        | undefined;
    if (!layer) {
        console.warn(`[fast-path] no map layer for layerId ${layerId}`);
        return false;
    }
    feature.layer = layer;
    return true;
};

/**
 * Forward direction (Lys → IB/Flytendemerke). Mirrors the relationship logic in
 * FeatureViewerContent.setPotentialSecondaryFeature.
 */
const resolveSecondaryFromLys = async (
    lys: Graphic
): Promise<{ secondary: Graphic | null; partnerMissing: boolean }> => {
    const klasse = lys.attributes?.klasse;
    let expectedRelationship: string | null = null;
    let mandatory = true;
    if (klasse === NauticalClasses.LB_Lysboeye) {
        expectedRelationship = "FlytendeMerke";
    } else if (klasse === NauticalClasses.HIB_HurtigbaatmerkeMedIndirekteBelysning) {
        expectedRelationship = "IB";
    } else if (klasse === NauticalClasses.FL_Fyrlykt || klasse === NauticalClasses.LA_Lanterne) {
        expectedRelationship = "IB";
        mandatory = false;
    }
    if (!expectedRelationship) return { secondary: null, partnerMissing: false };

    let related: { relationship: Relationship; features: Graphic[] }[];
    try {
        related = await getAllRelatedFeatures(lys);
    } catch (error) {
        console.error("[fast-path] failed to query related features from Lys:", error);
        return { secondary: null, partnerMissing: true };
    }
    const match = related.find((rel) => rel.relationship.name === expectedRelationship);
    if (!match || match.features.length === 0) {
        if (mandatory) {
            console.warn(`[fast-path] expected related ${expectedRelationship} but none found`);
        }
        return { secondary: null, partnerMissing: mandatory };
    }
    const secondary = match.features[0];
    if (!bindLayerById(secondary, match.relationship.relatedTableId)) return { secondary: null, partnerMissing: true };
    return { secondary, partnerMissing: false };
};

/**
 * Reverse direction (IB/Flytendemerke → Lys). Looks up the Lys partner from the
 * secondary's relationship list by `relatedTableId === FeatureClasses.Lys`, regardless of
 * relationship name. Returns null when the secondary is unpaired (e.g. an IB without
 * `tilknyttet_hib`).
 */
const resolvePrimaryFromSecondary = async (secondary: Graphic): Promise<Graphic | null> => {
    let related: { relationship: Relationship; features: Graphic[] }[];
    try {
        related = await getAllRelatedFeatures(secondary);
    } catch (error) {
        console.error("[fast-path] failed to query related features from secondary:", error);
        return null;
    }
    const match = related.find((rel) => rel.relationship.relatedTableId === FeatureClasses.Lys);
    if (!match || match.features.length === 0) return null;
    const lys = match.features[0];
    if (!bindLayerById(lys, FeatureClasses.Lys)) return null;
    return lys;
};

/**
 * Resolves the (primary, secondary) pair for direct-to-default purposes. Handles both
 * directions:
 *  - User selected a Lys (LB/HIB/FL klasse) → primary = selected, secondary = related partner.
 *  - User selected an IB or Flytendemerke that is paired (via relationship to Lys) →
 *    secondary = selected, primary = related Lys.
 *  - Anything else → primary = selected, secondary = null (single-feature fallback).
 *
 * "primary" is always the Lys-side feature when a pair is involved. The backend's
 * validation route runs against the Lys primary and covers the synced secondary's rules,
 * so the orchestrator can validate once via `primary`.
 */
export const resolveFastPathPair = async (selected: Graphic): Promise<FastPathPair> => {
    const selectedLayer = selected.layer as FeatureLayer | undefined;
    const selectedLayerId = selectedLayer?.layerId;

    if (selectedLayerId === FeatureClasses.Lys) {
        const { secondary, partnerMissing } = await resolveSecondaryFromLys(selected);
        return { primary: selected, secondary, partnerMissing };
    }

    if (selectedLayerId === FeatureClasses.IB || selectedLayerId === FeatureClasses.Flytendemerke) {
        const primary = await resolvePrimaryFromSecondary(selected);
        if (!primary) {
            return {
                primary: selected,
                secondary: null,
                partnerMissing: isLinkedSecondary(selectedLayerId, selected.attributes),
            };
        }
        return { primary, secondary: selected, partnerMissing: false };
    }

    return { primary: selected, secondary: null, partnerMissing: false };
};

/**
 * Back-compat shim for callers that still expect the Lys-only "secondary" resolver.
 * Returns the secondary side of the pair when the selected feature is on Lys, otherwise null.
 */
export const resolveFastPathSecondary = async (selected: Graphic): Promise<Graphic | null> => {
    const pair = await resolveFastPathPair(selected);
    // Only return the secondary when the selected feature IS the primary — preserves the
    // old contract for any caller that still uses the result for display purposes.
    if (pair.primary === selected) return pair.secondary;
    return null;
};

interface ResolvedSide {
    feature: Graphic;
    layerId: number;
    defaultFeature: Graphic | null;
    changes: FieldChange[];
    geometryChanged: boolean;
    statusField: string;
    previousStatus: unknown;
    restoreStatus: number;
}

/**
 * Re-fetches the version-side graphic by globalid so subsequent operations work against
 * committed data, not React-state-cached attributes. Mutates the passed graphic in place
 * so the layer binding is preserved.
 */
const refetchVersion = async (feature: Graphic, label: string): Promise<boolean> => {
    const layer = feature.layer as FeatureLayer;
    const globalid = feature.attributes?.globalid;
    if (typeof globalid !== "string" || globalid.length === 0) {
        finalizeProgressToast(`Mangler globalid på ${label}. Avbryter.`, "error");
        return false;
    }
    const query = layer.createQuery();
    query.where = `globalid = '${globalid}'`;
    query.outFields = ["*"];
    query.returnGeometry = true;
    try {
        const result = await layer.queryFeatures(query);
        const fresh = result.features[0];
        if (!fresh) {
            finalizeProgressToast(`Fant ikke ${label} i versjonen ved henting av ferskt data.`, "error");
            return false;
        }
        feature.attributes = fresh.attributes;
        feature.geometry = fresh.geometry;
        return true;
    } catch (error) {
        console.error(`Failed to refetch ${label} feature:`, error);
        finalizeProgressToast(`Kunne ikke hente ferskt data for ${label}. Avbryter.`, "error");
        return false;
    }
};

const computeGeometryChanged = (
    versionGeom: Geometry | null | undefined,
    defaultGeom: Geometry | null | undefined
): boolean => {
    const v = versionGeom ?? null;
    const d = defaultGeom ?? null;
    if (v === null && d === null) return false;
    if (v === null || d === null) return true;
    // equalsOperator.execute is typed against GeometryUnion (concrete subclasses) but at
    // runtime accepts any geometry instance; cast to bypass the structural-typing mismatch.
    return !equalsOperator.execute(v as never, d as never);
};

/**
 * Detects whether the light has ANY changed sector (Lyssektor 19 / Sektorlinje 2 /
 * Sektorflate 3) in the current version — added, edited, or deleted. The version-differences
 * endpoint is the authoritative "what changed" source; getAllLightHouseFeatures(lysVersion,
 * lysDefault) returns the light's version-side sectors (as featuresToDelete) and DEFAULT-side
 * sectors (as featuresToAdd). We union BOTH arrays per layer, so the resulting objectId set
 * covers inserts/updates (present version-side) AND deletes (present only DEFAULT-side)
 * regardless of which bucket they land in. ObjectIds are stable across branch versions, so
 * intersecting with the version's changed objectIds scopes the change to THIS light.
 *
 * No attribute/geometry comparison — presence in the change set is the signal. Returns true
 * to BLOCK. Fails CLOSED (returns true) on any error so an indeterminate state never lets a
 * sector-bearing change slip through the fast path.
 */
export const detectRelatedSectorChanges = async (
    versionGuid: string,
    lysVersion: Graphic,
    lysDefault: Graphic | null
): Promise<boolean> => {
    try {
        const layerId = (lysVersion.layer as FeatureLayer | undefined)?.layerId;
        if (layerId !== FeatureClasses.Lys) return false;

        const differences = await getDifferencesObjectIds(versionGuid, uuidv4(), false, true);
        const changedByLayer = buildChangedSectorMap(differences);
        if (changedByLayer.size === 0) return false; // no sector edits anywhere in the version

        const { featuresToAdd, featuresToDelete } = await getAllLightHouseFeatures(lysVersion, lysDefault ?? undefined);

        const lightSectorByLayer = new Map<number, Set<string>>();
        const collect = (layer: number, graphics: Graphic[]) => {
            const set = lightSectorByLayer.get(layer) ?? new Set<string>();
            for (const g of graphics) {
                const oid = g.attributes?.objectid;
                // Version/DEFAULT sector rows always carry an objectid; a row without one can't be matched against the differences set anyway.
                if (oid !== undefined && oid !== null) set.add(String(oid));
            }
            if (set.size > 0) lightSectorByLayer.set(layer, set);
        };
        collect(FeatureClasses.Lyssektor, [...featuresToAdd.lightSector, ...featuresToDelete.lightSector]);
        collect(FeatureClasses.Sektorlinje, [...featuresToAdd.sectorLine, ...featuresToDelete.sectorLine]);
        collect(FeatureClasses.Sektorflate, [...featuresToAdd.sectorArea, ...featuresToDelete.sectorArea]);

        return lightHasChangedSectors(changedByLayer, lightSectorByLayer);
    } catch (error) {
        console.error("[fast-path] sector-change detection failed; failing closed (blocking):", error);
        return true;
    }
};

/**
 * Orchestrates a direct-to-DEFAULT post for the selected feature and, when one exists,
 * its synced secondary partner (IB or Flytendemerke when the selection is on Lys).
 *
 * Owns a single progress toast (id: PROGRESS_TOAST_ID) that advances through stages so
 * the user sees what's happening. The caller MUST NOT raise its own success/error toast
 * for the same run; just react to the returned `FastPathResult` for control flow.
 *
 * Safety invariants:
 *  - Both sides are validated (using the stricter SKSD context when EITHER side requires
 *    SKSD) before any mutation lands.
 *  - Status restore writes happen before POST; if any write fails, prior restores are
 *    rolled back.
 *  - POST is a single multi-layer call so primary+secondary land together or not at all.
 *  - SKSD is a single pair-level send via the primary; a failed send leaves the post in
 *    place and creates a retry job (same order as the QA2 job flow).
 *  - A promotion to OPERATIVE only runs when the caller confirmed it (`promotionConfirmed`),
 *    so the user never posts past a warning they were not shown.
 */
export const runFastPath = async (
    selected: Graphic,
    versionGuid: string,
    versionName: string,
    options?: { promotionConfirmed?: boolean }
): Promise<FastPathResult> => {
    toast.info("Starter posting til Planroot...", {
        toastId: PROGRESS_TOAST_ID,
        progress: 0.05,
        autoClose: false,
    });

    // ---- Pre-A: resolve the pair (handles both "selected = Lys" and "selected = secondary").
    // `primary` is always the Lys-side feature when a pair is involved; the validation call
    // later runs against the primary regardless of which side the user clicked from.
    const { primary: feature, secondary, partnerMissing } = await resolveFastPathPair(selected);

    const primaryLayer = feature.layer as FeatureLayer;
    const primaryLayerId = primaryLayer?.layerId;
    if (primaryLayerId === undefined || primaryLayerId === null) {
        finalizeProgressToast("Mangler lag-informasjon på objektet.", "error");
        return { ok: false, reason: "post" };
    }

    // ---- Pre-B: re-fetch primary for ground truth ----
    toast.update(PROGRESS_TOAST_ID, {
        render: "Henter ferske data fra versjonen...",
        progress: 0.1,
    });
    if (!(await refetchVersion(feature, "hovedobjektet"))) {
        return { ok: false, reason: "post" };
    }

    // ---- Pre-C: re-fetch secondary, if any ----
    if (secondary) {
        if (!(await refetchVersion(secondary, "sekundærobjektet"))) {
            return { ok: false, reason: "post" };
        }
    }

    // ---- A: snapshot pre-post DEFAULT for both sides and compute the changesets ----
    toast.update(PROGRESS_TOAST_ID, {
        render: secondary ? "Sammenligner mot DEFAULT (hoved + sekundær)..." : "Sammenligner mot DEFAULT...",
        progress: 0.15,
    });
    const { defaultFeature: preDefaultPrimary } = await getDefaultAndOperativeFeature(feature, true);
    const primaryChanges = computeChangedFieldsVsDefault(feature, preDefaultPrimary);

    let preDefaultSecondary: Graphic | null = null;
    let secondaryChanges: FieldChange[] = [];
    if (secondary) {
        const result = await getDefaultAndOperativeFeature(secondary, true);
        preDefaultSecondary = result.defaultFeature;
        secondaryChanges = computeChangedFieldsVsDefault(secondary, preDefaultSecondary, SECONDARY_ONLY_IGNORED_FIELDS);
    }

    // ---- A.1: structured re-gate via the shared evaluator (single source of truth with
    // the UI). Runs on freshly re-fetched version data + freshly snapshotted DEFAULT and
    // returns BEFORE any mutation (the status restore in phase C is the first write). ----
    const primaryStatusField = getStatusFieldName(feature);
    const primaryStatus = primaryStatusField ? (feature.attributes[primaryStatusField] as number) : undefined;
    const primaryGeometryChanged = computeGeometryChanged(feature.geometry, preDefaultPrimary?.geometry);
    const primaryDefaultStatus =
        primaryStatusField && preDefaultPrimary
            ? (preDefaultPrimary.attributes?.[primaryStatusField] as number | undefined)
            : undefined;

    if (primaryStatus === undefined || !primaryStatusField) {
        const details = "Fant ikke statusfeltet for hovedobjektet.";
        finalizeProgressToast(`Avbryter: ${details}`, "error");
        return { ok: false, reason: "not-allowed", details };
    }

    let secondaryLayerId: number | undefined;
    let secondaryStatusField: string | null = null;
    let secondaryStatus: number | undefined;
    let secondaryGeometryChanged = false;
    let secondaryDefaultStatus: number | undefined;
    if (secondary) {
        secondaryLayerId = (secondary.layer as FeatureLayer).layerId ?? undefined;
        secondaryStatusField = getStatusFieldName(secondary);
        secondaryStatus = secondaryStatusField ? (secondary.attributes[secondaryStatusField] as number) : undefined;
        secondaryGeometryChanged = computeGeometryChanged(secondary.geometry, preDefaultSecondary?.geometry);
        secondaryDefaultStatus =
            secondaryStatusField && preDefaultSecondary
                ? (preDefaultSecondary.attributes?.[secondaryStatusField] as number | undefined)
                : undefined;
        if (secondaryStatus === undefined || !secondaryStatusField || secondaryLayerId === undefined) {
            const details = "Fant ikke lag- eller statusfelt for sekundærobjektet.";
            finalizeProgressToast(`Avbryter: ${details}`, "error");
            return { ok: false, reason: "not-allowed", details };
        }
    }

    toast.update(PROGRESS_TOAST_ID, {
        render: "Sjekker tilknyttede sektorer...",
        progress: 0.2,
    });
    const relatedSectorChanged = await detectRelatedSectorChanges(versionGuid, feature, preDefaultPrimary);

    const evaluation = evaluateFastPath(
        {
            layerId: primaryLayerId,
            versionStatus: primaryStatus,
            defaultStatus: primaryDefaultStatus,
            changes: primaryChanges,
            geometryChanged: primaryGeometryChanged,
        },
        secondary && secondaryLayerId !== undefined
            ? {
                  layerId: secondaryLayerId,
                  versionStatus: secondaryStatus as number,
                  defaultStatus: secondaryDefaultStatus,
                  changes: secondaryChanges,
                  geometryChanged: secondaryGeometryChanged,
              }
            : null,
        { relatedSectorChanged, partnerMissing, ...fastPathConfigOptions() }
    );

    if (!evaluation.canPost) {
        const details =
            evaluation.reasons.length > 0
                ? evaluation.reasons.map(fastPathReasonText).join(" ")
                : "Ingen endringer å poste — verken hovedobjekt eller sekundærobjekt har endringer.";
        finalizeProgressToast(`Avbryter: ${details}`, "error");
        return { ok: false, reason: "not-allowed", details };
    }

    // The UI gate ran on cached state; if fresh data now promotes to OPERATIVE but the user
    // confirmed without that warning, stop rather than post past it.
    if (evaluation.promotesToOperative && options?.promotionConfirmed !== true) {
        const details =
            "Objektet vil bli satt til Operativ, men dette ble ikke bekreftet. Planroot kan ha endret seg — prøv på nytt.";
        finalizeProgressToast(`Avbryter: ${details}`, "error");
        return { ok: false, reason: "not-allowed", details };
    }

    const anySksdRequired = evaluation.sksdWillSend;

    // ---- B: validate via the primary only.
    // The backend's validation route runs against the primary (Lys) feature and includes
    // the rules that cross-reference the synced secondary. A separate validation call on
    // the secondary would be redundant and could surface duplicate / confusing errors.
    // Picking the stricter SKSD context when EITHER side requires SKSD preserves the
    // existing safety invariant ("don't post something SKSD will later reject").
    toast.update(PROGRESS_TOAST_ID, {
        render: anySksdRequired ? "Validerer endring (SKSD-regler)..." : "Validerer endring...",
        progress: 0.3,
    });
    const validationContext = anySksdRequired ? "SendToSKSD" : "PostToDefault";

    let validation;
    try {
        validation = await getValidationResult(
            feature.attributes.globalid,
            primaryLayerId,
            versionName,
            validationContext
        );
    } catch (error) {
        console.error("Validation request failed:", error);
        finalizeProgressToast("Validering feilet. Kunne ikke poste til planroot.", "error");
        return { ok: false, reason: "validation", errors: ["Kunne ikke kontakte valideringstjenesten."] };
    }
    if (!validation.isValid) {
        const errorMessages = (validation.errors ?? []).map((e) => {
            const attribute = e.attributeName || "Ukjent felt";
            const detail = e.errorMessage ? `: ${e.errorMessage}` : "";
            const source = e.relatedFeatureClassName ? ` (${e.relatedFeatureClassName})` : "";
            return `${attribute}${source}${detail}`;
        });
        finalizeProgressToast(`Valideringen feilet (${errorMessages.length} feil). Se detaljer under.`, "error");
        for (const msg of errorMessages) {
            toast.error(`Valideringsfeil — ${msg}`, { autoClose: false, className: "toast-error" });
        }
        return { ok: false, reason: "validation", errors: errorMessages };
    }

    // ---- C: restore each side's status to its planroot value, or OPERATIVE when sent to SKSD (with full rollback) ----
    toast.update(PROGRESS_TOAST_ID, {
        render: "Forbereder posting (oppdaterer status)...",
        progress: 0.45,
    });

    if (!primaryStatusField) {
        finalizeProgressToast("Kunne ikke finne statusfeltet for hovedobjektet.", "error");
        return { ok: false, reason: "post" };
    }
    const sides: ResolvedSide[] = [
        {
            feature,
            layerId: primaryLayerId,
            defaultFeature: preDefaultPrimary,
            changes: primaryChanges,
            geometryChanged: primaryGeometryChanged,
            statusField: primaryStatusField,
            previousStatus: feature.attributes[primaryStatusField],
            restoreStatus: (evaluation.perSideRestore.primary ?? NFSStatus.OPERATIVE) as number,
        },
    ];
    if (secondary && secondaryStatusField && secondaryLayerId !== undefined) {
        sides.push({
            feature: secondary,
            layerId: secondaryLayerId,
            defaultFeature: preDefaultSecondary,
            changes: secondaryChanges,
            geometryChanged: secondaryGeometryChanged,
            statusField: secondaryStatusField,
            previousStatus: secondary.attributes[secondaryStatusField],
            restoreStatus: (evaluation.perSideRestore.secondary ?? NFSStatus.OPERATIVE) as number,
        });
    } else if (secondary && (!secondaryStatusField || secondaryLayerId === undefined)) {
        finalizeProgressToast("Kunne ikke finne lag- eller statusfelt for sekundærobjektet.", "error");
        return { ok: false, reason: "post" };
    }

    // Track which sides we already wrote so we can roll back precisely.
    const rolledBack: ResolvedSide[] = [];
    const rollback = async (until: ResolvedSide[]) => {
        for (const side of until) {
            side.feature.attributes[side.statusField] = side.previousStatus;
            await updateFeatureAttributes(side.feature);
        }
    };

    for (const side of sides) {
        // Skip the write when the side already holds its restore target (e.g. an unchanged
        // secondary already aligned with DEFAULT).
        if (side.previousStatus === side.restoreStatus) continue;
        side.feature.attributes[side.statusField] = side.restoreStatus;
        const updated = await updateFeatureAttributes(side.feature);
        if (!updated) {
            await rollback(rolledBack);
            finalizeProgressToast("Kunne ikke oppdatere status på objektet(ene). Avbryter posting.", "error");
            return { ok: false, reason: "post" };
        }
        rolledBack.push(side);
    }

    // ---- D: POST version → DEFAULT (single multi-layer call) ----
    toast.update(PROGRESS_TOAST_ID, {
        render: "Poster til DEFAULT...",
        progress: 0.6,
    });
    // De-dup: if both sides happen to be on the same layer (shouldn't, but defense-in-depth)
    // we still want a single layer entry with both objectids.
    const postPayload = sides.map((s) => ({
        feature: s.feature,
        layerId: s.layerId,
    }));
    const postOk = await postFeaturesToDefault(
        postPayload.map((p) => p.feature),
        versionGuid
    );
    if (!postOk) {
        await rollback(rolledBack);
        finalizeProgressToast("Feil under posting til planroot. Endringen er ikke sendt.", "error");
        return { ok: false, reason: "post" };
    }

    // ---- E: SKSD transfer (primary only — backend joins the pair, matching validation) ----
    // The SKSD endpoint takes the primary feature and bundles the joined secondary on its
    // side. Sending the secondary independently would re-transfer data the backend already
    // included, and could race with the primary's transfer. Mirrors the validation step
    // above which runs against the primary for the same reason. `anySksdRequired` is the
    // pair-level gate: either side carrying an SKSD-trigger change is enough, because the
    // single primary send covers both.
    if (!anySksdRequired) {
        finalizeProgressToast(
            secondary ? "Endring postet til DEFAULT (hoved + sekundær)." : "Endring postet til DEFAULT.",
            "success",
            5000
        );
        return { ok: true, sksdSent: false, hadSecondary: !!secondary };
    }

    toast.update(PROGRESS_TOAST_ID, {
        render: "Henter postet objekt fra DEFAULT...",
        progress: 0.75,
    });

    const primarySide = sides[0];
    const { defaultFeature: postDefault } = await getDefaultAndOperativeFeature(primarySide.feature, true);
    const defaultGlobalId = postDefault?.attributes?.globalid as string | undefined;

    toast.update(PROGRESS_TOAST_ID, {
        render: "Sender til SKSD...",
        progress: 0.9,
    });

    let sksdResult: { sent: boolean; recoveryJobId: string | null };
    if (!defaultGlobalId) {
        const jobId = await createFailedSksdJobForFeature(
            primarySide.feature,
            "Fant ikke hovedobjekt i DEFAULT etter posting"
        );
        sksdResult = { sent: false, recoveryJobId: jobId };
    } else {
        try {
            const result = await exportFeatureToSksd(primarySide.layerId, defaultGlobalId);
            if (result.success) {
                sksdResult = { sent: true, recoveryJobId: null };
            } else {
                const jobId = await createFailedSksdJobForFeature(
                    primarySide.feature,
                    result.message ?? "Ukjent SKSD-feil"
                );
                sksdResult = { sent: false, recoveryJobId: jobId };
            }
        } catch (error) {
            console.error("SKSD send threw:", error);
            const message = error instanceof Error ? error.message : "Ukjent SKSD-nettverksfeil";
            const jobId = await createFailedSksdJobForFeature(primarySide.feature, message);
            sksdResult = { sent: false, recoveryJobId: jobId };
        }
    }

    if (sksdResult.sent) {
        finalizeProgressToast(
            secondary
                ? "Endring postet til DEFAULT og sendt til SKSD (hoved + sekundær)."
                : "Endring postet til DEFAULT og sendt til SKSD.",
            "success",
            5000
        );
        return { ok: true, sksdSent: true, hadSecondary: !!secondary };
    }

    finalizeProgressToast(
        sksdResult.recoveryJobId
            ? "Postet til DEFAULT, men SKSD feilet — retry-jobb opprettet."
            : "Postet til DEFAULT, men SKSD feilet — kunne ikke opprette retry-jobb.",
        "warning"
    );
    return {
        ok: false,
        reason: "sksd",
        primaryRecoveryJobId: sksdResult.recoveryJobId,
    };
};
